import React, { useMemo, useState } from 'react';
import {
  Grid,
  Input,
  InputLabel,
  FormHelperText,
  FormControl,
  IconButton,
  Collapse,
  Paper,
  InputAdornment,
} from '@mui/material';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTextSlash } from '@fortawesome/free-solid-svg-icons';
import { CommandPanels, EditorControls, withBaseEditor } from '../../BaseEditor';

function LandEditor({
  getControl, currentUser, dataSetter, fields, data, erroredFields,
}) {
  const currentOrg = useMemo(
    () => (currentUser.is_superuser ? data.org && (data.org.id || null) : currentUser.org.id),
    [currentUser.is_superuser, currentUser.org.id, data.org],
  );
  const localityFilter = useMemo(
    () => ({
      region__org: currentOrg,
    }),
    [currentOrg],
  );
  const [addrEditMode, setAddrEditMode] = useState(1);
  const localityAreaFilter = useMemo(
    () => ({ locality: data.locality && data.locality.id }),
    [data.locality],
  );
  const streetFilter = useMemo(
    () => ({ locality: data.locality && data.locality.id }),
    [data.locality],
  );
  const houseFilter = useMemo(
    () => ({ street: data.street && data.street.id }),
    [data.street],
  );
  return (
    <Grid container spacing={2}>
      <Grid item sm={12}>
        <Collapse in={addrEditMode === 1}>
          <FormControl fullWidth margin="dense">
            <InputLabel htmlFor="addr_input" error={!!erroredFields.address}>{fields.address.label}</InputLabel>
            <Input
              id="addr_input"
              value={data.address}
              onChange={(e) => dataSetter({ address: e.target.value })}
              error={!!erroredFields.address}
            // multiline
            // rows={3}
              margin="dense"
              endAdornment={(
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle to address classifier"
                    onClick={() => setAddrEditMode(2)}
                  >
                    <FontAwesomeIcon icon={faEdit} size="xs" />
                  </IconButton>
                </InputAdornment>
              )}
            />
            <FormHelperText error>{erroredFields.address}</FormHelperText>
          </FormControl>
        </Collapse>
        <Collapse in={addrEditMode === 2}>
          <Paper sx={{
            display: 'flex', px: 2, py: 1, gap: 1,
          }}
          >
            {getControl('locality')}
            {getControl('locality_area', { filter: localityAreaFilter })}
            {getControl('street', { filter: streetFilter })}
            {getControl('house', { filter: houseFilter })}
            {getControl('number')}
            <IconButton
              aria-label="toggle to address classifier"
              onClick={() => setAddrEditMode(1)}
              edge="end"
            >
              <FontAwesomeIcon icon={faTextSlash} size="xs" />
            </IconButton>
          </Paper>
        </Collapse>
      </Grid>
      <Grid item xl={4} sm={6}>
        {getControl('area', { precision: 5 })}
      </Grid>

      <Grid item xl={4} sm={6}>
        {getControl('percent')}
      </Grid>
      <Grid item xl={4} sm={12}>
        {getControl('locality', { filter: localityFilter })}
      </Grid>
      <Grid item sm={12}>
        <EditorControls.TextInput
          label={fields.notes.label}
          readOnly={fields.notes.read_only}
          required={fields.notes.required}
          value={data.notes}
          onChange={(e, v) => dataSetter({ notes: v })}
        />
      </Grid>
      {currentUser.is_superuser && (
        <Grid item sm={12}>
          {getControl('org', {
            backend: '/api/catalog/orgs/',
          })}
        </Grid>
      )}
    </Grid>
  );
}
LandEditor.propTypes = {
  getControl: PropTypes.func.isRequired,
  data: PropTypes.shape({
    id: PropTypes.number,
    // landestimatedvalue_set: PropTypes.arrayOf(PropTypes.shape({})),
    // number: PropTypes.string,
    org: PropTypes.shape({
      id: PropTypes.number,
    }),
    locality: PropTypes.shape({
      id: PropTypes.number,
      koatuu: PropTypes.string,
    }),
    locality_area: PropTypes.shape({
      id: PropTypes.number,
      repr: PropTypes.string,
    }),
    street: PropTypes.shape({
      id: PropTypes.number,
      repr: PropTypes.string,
    }),
    house: PropTypes.shape({
      id: PropTypes.number,
      repr: PropTypes.string,
    }),
    number: PropTypes.string,
    address: PropTypes.string,
    percent: PropTypes.number,
    notes: PropTypes.string,
  }).isRequired,
  dataSetter: PropTypes.func.isRequired,
  currentUser: PropTypes.shape().isRequired,
  fields: PropTypes.shape({
    address: PropTypes.shape({
      label: PropTypes.string,
      required: PropTypes.bool,
      read_only: PropTypes.bool,
      max_length: PropTypes.number,
    }),
    locality: PropTypes.shape({
      label: PropTypes.string,
      required: PropTypes.bool,
      read_only: PropTypes.bool,
      resource: PropTypes.string,
    }),
    locality_area: PropTypes.shape({
      label: PropTypes.string,
      required: PropTypes.bool,
      read_only: PropTypes.bool,
      resource: PropTypes.string,
    }),
    street: PropTypes.shape({
      label: PropTypes.string,
      required: PropTypes.bool,
      read_only: PropTypes.bool,
      resource: PropTypes.string,
    }),
    house: PropTypes.shape({
      label: PropTypes.string,
      required: PropTypes.bool,
      read_only: PropTypes.bool,
      resource: PropTypes.string,
    }),
    notes: PropTypes.shape({
      label: PropTypes.string,
      required: PropTypes.bool,
      read_only: PropTypes.bool,
    }),
  }).isRequired,
  erroredFields: PropTypes.shape({
    address: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

export default withBaseEditor(LandEditor, '/api/realty/easements/', CommandPanels.catCommandPanel);
